import {
  EXECUTIVE_OFFICER_AS_EMPLOYEE,
  EXECUTIVE_OFFICER_SELF_EMPLOYED
} from '@libs/data-access/entities'
import { useAccountFlipperFeatureFlags } from '@libs/data-access/queries'
import {
  Stack,
  Badge,
  Avatar,
  Flex,
  IconButton,
  CommonFileTextStarIcon,
  Text,
  Tooltip
} from '@libs/ui/ds'
import { API_APPLICATION_HOST, HTTP_PROTOCOL } from '@libs/utils/environments'
import { usePlanningTranslation } from '@libs/utils/translations'
import type { PlanningsRouteParams } from '@spa/core/router'
import { useMemoSelector } from '@spa/redux/hooks'
import { isImpersonationContext } from '@spa/scenes/Auth/shared/services/authState/authServiceUtils'
import {
  contractsSelectors,
  getParams,
  membershipsSelectors,
  usePlanningPolicies
} from '@spa/scenes/Plannings/redux/selectors'
import moment from 'moment'
import type { FC } from 'react'
import React from 'react'
import { useParams } from 'react-router-dom'
import { getCurrentAccount } from 'snap-redux/modules/accounts/selectors'

import { routeUpdateController } from '../../redux/router/utils'

import EmployeeCounters from './EmployeeCounters'
import ShowEmployeeCounters from './EmployeeCounters/ShowEmployeeCounters/ShowEmployeeCounters'
import EmployeeLink from './EmployeeLink'

type Props = {
  contractId: number
  alertsComponent?: React.ReactNode
}

const MembershipCellComponent: FC<Props> = ({
  contractId,
  alertsComponent
}) => {
  const { t, tCommon } = usePlanningTranslation()
  const params = useParams<PlanningsRouteParams>()
  const account = useMemoSelector(state => getCurrentAccount(state))
  const contract = contractsSelectors.useGetCachedById(contractId)

  const isExecutiveOfficer = contract
    ? contract.contract_type === EXECUTIVE_OFFICER_SELF_EMPLOYED ||
      contract.contract_type === EXECUTIVE_OFFICER_AS_EMPLOYEE
    : false

  const membership = membershipsSelectors.useGetCachedById(
    contract?.membership_id
  )

  const policies = usePlanningPolicies()
  const hideCountersToDailyWorker =
    contract.daily_worker &&
    account.hide_counters_alerts_planning_to_daily_worker

  const { date } = useMemoSelector(getParams)
  const startWeek = moment(date).startOf('isoWeek').format('YYYY-MM-DD')
  const endWeek = moment(date).endOf('isoWeek').format('YYYY-MM-DD')
  // @ts-ignore TO DO: fix (strictNullChecks errors) (https://snapshiftapp.atlassian.net/browse/COP-333)
  const { core_engine_hide_ticket_caisse } = useAccountFlipperFeatureFlags()
  const currentUserIsImpersonated = isImpersonationContext()

  const isMinorEligible = () => {
    if (
      params.view === routeUpdateController.VIEWS.WEEK ||
      params.view === routeUpdateController.VIEWS.DAY
    ) {
      return membership.minor_employee
    }

    return false
  }

  return (
    <>
      <Stack gap={1} col>
        {isMinorEligible() && (
          <Text className=' text-xxs text-neutral-500'>
            {t('weekly.team.row.minorEmployee')}
          </Text>
        )}
        <Flex row className='items-center overflow-hidden'>
          <Avatar
            size='s'
            src={membership?.avatar_urls?.thumb}
            labels={[membership?.firstname, membership?.lastname]}
          />
          <div className='ml-2'>
            <EmployeeLink membershipId={membership?.id} />
          </div>
          {currentUserIsImpersonated && !core_engine_hide_ticket_caisse && (
            <Tooltip
              id='modulation'
              position='top'
              label={tCommon('action.ticketCaisse')}
              className='ml-2'
            >
              <a
                rel='noopener noreferrer'
                href={`${HTTP_PROTOCOL}://${API_APPLICATION_HOST}/api/v2/payroll/ticket?membership_id=${contract.membership_id}&period=${startWeek}..${endWeek}`}
                target='_blank'
                className='text-neutral-600'
              >
                <IconButton
                  intent='neutral-dark'
                  variant='tertiary'
                  icon={<CommonFileTextStarIcon />}
                  size='s'
                />
              </a>
            </Tooltip>
          )}
        </Flex>
        <Flex row className='items-center gap-1'>
          {policies.canSeeDailyWorkerBadge &&
            hideCountersToDailyWorker &&
            !isExecutiveOfficer && (
              <Badge
                label={tCommon('models.contractType.dailyWorker')}
                color='semantic.blue'
                variant='light'
                size='s'
              />
            )}
          {isExecutiveOfficer && (
            <Badge
              label={t('weekly.team.row.executiveOfficer')}
              color='secondary.purple'
              variant='light'
              size='s'
            />
          )}
          {!hideCountersToDailyWorker && !isExecutiveOfficer && (
            <ShowEmployeeCounters contractId={contract.id}>
              <EmployeeCounters contractId={contract.id} />
            </ShowEmployeeCounters>
          )}
        </Flex>
      </Stack>
      {alertsComponent}
    </>
  )
}
export const MembershipCell = React.memo(MembershipCellComponent)
